import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@farmers-fridge/ui-library';
import FFLink from '../FFLink';
import FloatingLabel from '../FloatingLabel';
import IllustrationSpeechBubble from '../icons/IllustrationSpeechBubble';
import formatPhoneNumber, {
  sanitizePhoneNumber,
} from '../../utils/formatPhoneNumber';
import USAStatesList from '../../utils/USAStates';
import useSiteMetadata from '../../utils/useSiteMetaData';
import useSegment, { EVENTS } from '../../utils/useSegment';
import styles from './SMSFooter.module.css';

/** @type {Object<string, string>} */
const ERROR_MESSAGES = {
  INVALID_PHONE_NUMBER: 'Please enter a valid phone number',
  NOT_ALLOWED_PHONE_NUMBER:
    "Sorry, we can't send SMS messages to this phone number",
  UNKNOWN_ERROR: 'Something went wrong, please try again',
};

export default function SMSFooter() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [stateCode, setStateCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formCompleted, setFormCompleted] = useState(false);
  /** @type {[(string|undefined), React.Dispatch<React.SetStateAction<(string|undefined)>>]} */
  const [phoneError, setPhoneError] = useState();
  const [isInFocus, setIsInFocus] = useState(false);
  const { awsApiGatewayUrl } = useSiteMetadata();
  const { pushTrackEvent } = useSegment();

  /** @param {React.FormEvent} event */
  async function handleSubmit(event) {
    event.preventDefault();

    let sanitizedPhoneNumber;
    try {
      sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);
    } catch (e) {
      setPhoneError('INVALID_PHONE_NUMBER');
      return;
    }

    setPhoneError(undefined);
    setIsSubmitting(true);

    const phoneNumberWithCountryCode = `+1${sanitizedPhoneNumber}`;

    const segmentEventObj = {
      accepts_SMS: true,
      call_to_action_name: 'WebFooter',
      phone: phoneNumberWithCountryCode,
      state: stateCode,
    };
    try {
      await fetch(`${awsApiGatewayUrl}/Query/customer`, {
        body: JSON.stringify({
          address: { stateCode },
          entrySourceFF: ['webFooter'],
          phoneNumber: phoneNumberWithCountryCode,
          smsPromotions: true,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }).then(async res => {
        if (!res.ok) {
          const responseBody = await res.json();
          throw new Error(responseBody.message);
        }
        return res.json();
      });

      setFormCompleted(true);
      pushTrackEvent(EVENTS.PHONE_SIGN_UP, {
        ...segmentEventObj,
        success: true,
      });
    } catch (/** @type {any} error */ error) {
      setPhoneError(error.message || 'UNKNOWN_ERROR');
      pushTrackEvent(EVENTS.PHONE_SIGN_UP, {
        ...segmentEventObj,
        success: false,
        failure_reason: error.message,
      });
    }
    setIsSubmitting(false);
  }

  useEffect(() => {
    /** @type {HTMLInputElement|null} phoneInputEl */
    const phoneInputEl = document.querySelector('input#webFooter-phoneNumber');
    if (phoneInputEl?.value) {
      setPhoneNumber(phoneInputEl.value);

      try {
        // throws error if not a valid phone number
        sanitizePhoneNumber(phoneInputEl.value);
        setIsPhoneNumberValid(true);
      } catch {
        setIsPhoneNumberValid(false);
      }
    }

    /** @type {HTMLSelectElement|null} stateCodeInputEl */
    const stateCodeInputEl = document.querySelector(
      'select#webFooter-stateCode',
    );
    if (stateCodeInputEl?.value) {
      setStateCode(stateCodeInputEl.value);
    }
  }, []);

  /** @type {React.MutableRefObject<HTMLParagraphElement|null>} successMessageRef */
  const successMessageRef = useRef(null);
  useEffect(() => {
    if (formCompleted && successMessageRef.current) {
      successMessageRef.current.focus();
    }
  }, [formCompleted, successMessageRef.current]);

  return (
    <section className={styles.wrapper}>
      <div className={styles.topSection}>
        <h2 className={styles.header}>Sign Up for Texts</h2>
        <p className={styles.subHeader}>
          Join our SMS list to be the first to find out about flash promos, new
          menu drops, giveaways, and more. P.S. We promise we won&apos;t reach
          out too often, only for the big stuff.
        </p>
        <div className={styles.icon}>
          <IllustrationSpeechBubble />
        </div>
      </div>
      {!formCompleted && (
        <>
          <form className={styles.form} onSubmit={handleSubmit}>
            <FloatingLabel
              className={styles.inputWrapper}
              error={
                phoneError
                  ? ERROR_MESSAGES[phoneError] || ERROR_MESSAGES.UNKNOWN_ERROR
                  : undefined
              }
            >
              <input
                aria-label="Phone Number"
                id="webFooter-phoneNumber"
                name="phoneNumber"
                onChange={e => {
                  setPhoneError(undefined);
                  setPhoneNumber(e.target.value);
                }}
                onFocus={() => setIsInFocus(true)}
                onBlur={() => setIsInFocus(false)}
                required
                type="tel"
                value={
                  !isInFocus && isPhoneNumberValid
                    ? formatPhoneNumber(phoneNumber)
                    : phoneNumber
                }
              />
            </FloatingLabel>

            <FloatingLabel className={styles.inputWrapper}>
              <select
                aria-label="State"
                onChange={e => {
                  setStateCode(e.target.value);
                }}
                id="webFooter-stateCode"
                name="stateCode"
                required
              >
                <option key="blank" value="">
                  {' '}
                </option>
                {USAStatesList.map(
                  ({ alias = false, stateName, shortCode }) => {
                    if (alias) {
                      return null;
                    }
                    return (
                      <option key={shortCode} value={shortCode}>
                        {stateName}
                      </option>
                    );
                  },
                )}
              </select>
            </FloatingLabel>

            <Button
              className={styles.submitButton}
              buttonType="primary"
              disabled={!phoneNumber || !stateCode}
              loading={isSubmitting}
              type="submit"
            >
              Join
            </Button>
          </form>
          <p className={styles.termsDisclaimer}>
            See our{' '}
            <FFLink className={styles.termsLink} url="/terms">
              Terms and Conditions
            </FFLink>
          </p>
        </>
      )}
      {formCompleted && (
        <>
          <p
            className={styles.successConfirmation}
            ref={successMessageRef}
            tabIndex={-1}
          >
            Thank you for subscribing. You signed up for SMS communication from
            Farmer&apos;s Fridge. The phone number you subscribed is{' '}
            <span className={styles.successPhone}>
              {formatPhoneNumber(phoneNumber)}
            </span>
            .
          </p>
          <p className={styles.successUnsubscribe}>
            Any marketing messages you receive from this sender will have an
            unsubscribe option.
          </p>
        </>
      )}
    </section>
  );
}
